import { useState, useEffect, useCallback } from 'react';
import feedbackService from '../services/feedbackService';

/**
 * Custom hook for handling feedback retry logic and error recovery
 */
const useFeedbackRetry = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);

  // Check for pending backups on mount and periodically
  useEffect(() => {
    const checkPendingBackups = () => {
      const count = feedbackService.getPendingBackupCount();
      setPendingCount(count);
    };

    // Check immediately
    checkPendingBackups();

    // Check every 30 seconds
    const interval = setInterval(checkPendingBackups, 30000);

    return () => clearInterval(interval);
  }, []);

  // Retry failed submissions
  const retryFailedSubmissions = useCallback(async () => {
    if (isRetrying || pendingCount === 0) return;

    setIsRetrying(true);
    setRetryError(null);

    try {
      const result = await feedbackService.retryFailedSubmissions();
      
      if (result.success) {
        setPendingCount(result.remainingCount);
        
        if (result.retriedCount > 0) {
          console.log(`Successfully retried ${result.retriedCount} feedback submissions`);
        }
        
        return {
          success: true,
          retriedCount: result.retriedCount,
          remainingCount: result.remainingCount
        };
      } else {
        setRetryError(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to retry submissions';
      setRetryError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsRetrying(false);
    }
  }, [isRetrying, pendingCount]);

  // Auto-retry when coming back online
  useEffect(() => {
    const handleOnline = () => {
      if (pendingCount > 0) {
        setTimeout(() => {
          retryFailedSubmissions();
        }, 1000); // Wait 1 second after coming online
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [pendingCount, retryFailedSubmissions]);

  return {
    pendingCount,
    isRetrying,
    retryError,
    retryFailedSubmissions
  };
};

export default useFeedbackRetry;